// SINGLE SOURCE OF TRUTH for programme eligibility, vast exclusivity, and vast defaults.
//
// Pure functions only: no React, no DB, no fetch, no I/O. Both the client
// (src/client/components/school-portal/sections/VastKeuzeSection.tsx) and the server
// (src/server/routes/school/selections.ts) import these so the eligibility / per-leerjaar
// vast exclusivity / default-on rules stay identical on both sides.
//
// "Year level" is a leerjaar code like "groep_6". A group's leerjaren is the set of codes the
// class group contains. An offering's "year levels" are the leerjaren that make it eligible
// (who sees the card); its "participating" leerjaren are the ones that may actually TAKE PART
// once eligible (combi participation), falling back to its year_levels when not given.

// ─── Plain-data shapes (no ORM / no DB rows) ────────────────────────────────────────────────

/** A leerjaar of a group, identified by its code (e.g. "groep_6"). */
export type GroupYL = { code: string };

/** A class group with the leerjaren it contains. */
export type GroupRules = { yearLevels: GroupYL[] };

/**
 * The eligibility/exclusivity-relevant facts about one programme offering. This is intentionally
 * a thin projection of programme_offerings so callers can build it from a DB row OR from the
 * /eligible payload without coupling to the full schema type.
 */
export type OfferingRules = {
  id: number;
  enrolmentModeCode: string;
  /** If set, the group must HAVE at least one of these leerjaren (on top of the year_levels match). */
  requiredYearLevels: string[] | null;
  /** Which leerjaren may take part once eligible. Null -> falls back to the offering's year_levels. */
  participatingYearLevels: string[] | null;
  /** EXACT-COMPOSITION cap: every one of the group's leerjaren must be within this set. */
  maxYearLevels: string[] | null;
  /** Stackable vaste voorstellingen are exempt from per-leerjaar vast exclusivity. */
  vastStackable: boolean;
};

/** Per-offering assignment of a group's leerjaren to an offering (offeringId -> yearLevels[]). */
export type VastAssignment = Map<number, string[]>;

// ─── Helpers ────────────────────────────────────────────────────────────────────────────────

/** The leerjaren that may TAKE PART for an offering: participatingYearLevels, else its year_levels. */
function participatingCodes(offering: OfferingRules, offeringYearLevels: string[]): Set<string> {
  const codes = offering.participatingYearLevels?.length ? offering.participatingYearLevels : offeringYearLevels;
  return new Set(codes);
}

// ─── Eligibility ────────────────────────────────────────────────────────────────────────────

/**
 * Whether `offering` is eligible for `group`. Mirrors VastKeuzeSection's eligibleOfferings rule
 * exactly:
 *  - the group has >= 1 of the offering's year_levels (content match); AND
 *  - (no required_year_levels OR the group has one of them); AND
 *  - (no max_year_levels OR EVERY one of the group's leerjaren is within the max set); AND
 *  - the offering is not blocked for this school.
 *
 * @param offering            the offering's eligibility-relevant facts
 * @param offeringYearLevels  the offering's year_levels (the codes that make it eligible)
 * @param groupYearLevels     the group's leerjaren codes
 * @param blocked             true when this offering is blocked for the school
 */
export function isOfferingEligibleForGroup(
  offering: OfferingRules,
  offeringYearLevels: string[],
  groupYearLevels: string[],
  blocked: boolean,
): boolean {
  if (blocked) return false;
  const groupCodes = new Set(groupYearLevels);
  // Content match: the group must share at least one of the offering's year_levels.
  if (!offeringYearLevels.some((c) => groupCodes.has(c))) return false;
  // Strict combi rule: if the offering requires certain leerjaren, the group must HAVE one.
  if (offering.requiredYearLevels?.length && !offering.requiredYearLevels.some((c) => groupCodes.has(c))) return false;
  // Exact-composition rule: every one of the group's leerjaren must be within the max set.
  if (offering.maxYearLevels?.length && !groupYearLevels.every((c) => offering.maxYearLevels!.includes(c))) return false;
  return true;
}

/**
 * The leerjaren of `group` that may take part in `offering` (the eligible-and-participating set).
 * Equivalent to VastKeuzeSection's eligibleYLs: the group's leerjaren intersected with the
 * offering's participating codes.
 */
export function eligibleYearLevelsForGroup(
  offering: OfferingRules,
  offeringYearLevels: string[],
  group: GroupRules,
): string[] {
  const part = participatingCodes(offering, offeringYearLevels);
  return group.yearLevels.filter((y) => part.has(y.code)).map((y) => y.code);
}

// ─── Vast per-leerjaar exclusivity (GLOBAL, across ALL the group's eligible vaste) ───────────

/**
 * Apply a vast claim: the claimer offering takes `claimedYearLevels` of `group`, and those
 * leerjaren are STRIPPED from every OTHER non-stackable vaste voorstelling in the assignment
 * (dropping any offering left with no leerjaren). Stackable vaste are left untouched, so they
 * can always coexist.
 *
 * The exclusivity is GLOBAL per leerjaar across ALL of the group's eligible vaste, NOT scoped to
 * a choice-set: each leerjaar of the group may belong to at most ONE non-stackable vaste.
 *
 * @param currentAssignment  the current offeringId -> yearLevels[] map (only this group's vaste)
 * @param group              the group whose leerjaren are being assigned
 * @param claimerId          the offering taking the claimed leerjaren
 * @param claimedYearLevels  the leerjaren the claimer now owns (already filtered to eligible)
 * @param allEligibleVaste   every vaste offering eligible for the group (to know stackability)
 * @returns a NEW assignment map (the input is not mutated). Empty offerings are dropped.
 */
export function resolveVastClaim(
  currentAssignment: VastAssignment,
  group: GroupRules,
  claimerId: number,
  claimedYearLevels: string[],
  allEligibleVaste: OfferingRules[],
): VastAssignment {
  const stackableById = new Map(allEligibleVaste.map((o) => [o.id, o.vastStackable]));
  const claimerStackable = stackableById.get(claimerId) ?? false;
  const claimed = new Set(claimedYearLevels);
  const next: VastAssignment = new Map();

  for (const [offId, yls] of currentAssignment) {
    if (offId === claimerId) continue; // re-added below with the claimed set
    // Stackable offerings are exempt from exclusivity: keep their leerjaren as-is.
    if (stackableById.get(offId) ?? false) {
      if (yls.length) next.set(offId, [...yls]);
      continue;
    }
    // Non-stackable: lose any of the claimer's leerjaren. (A stackable claimer steals nothing,
    // so non-stackable peers keep everything — stackable + non-stackable freely coexist.)
    const remaining = claimerStackable ? [...yls] : yls.filter((c) => !claimed.has(c));
    if (remaining.length) next.set(offId, remaining);
  }

  // The claimer takes exactly claimedYearLevels (empty => it is dropped, i.e. deselected).
  if (claimedYearLevels.length) next.set(claimerId, [...claimedYearLevels]);

  return next;
}

// ─── Vast defaults (which vaste are default-ON for an untouched group) ───────────────────────

/**
 * The default vaste assignment for an UNTOUCHED group: which vaste are auto-selected, with which
 * leerjaren, when the school has made no vast decision yet.
 *
 * Rules:
 *  - Stackable vaste are ALWAYS default-on (for all their eligible leerjaren of the group).
 *  - A non-stackable vaste is auto-selected for a leerjaar only if it is the ONLY non-stackable
 *    vaste eligible for THAT leerjaar (uncontested). When 2+ non-stackable vaste compete for a
 *    leerjaar, that leerjaar is left undecided (the school must choose), so it is omitted from
 *    every competing offering's default.
 *
 * @param group           the group
 * @param eligibleVaste   the group's eligible vaste, each carrying its own year_levels so we can
 *                        compute the participating leerjaren per offering
 * @returns offeringId -> yearLevels[] for the default-on assignment (offerings with no default
 *          leerjaar are omitted entirely).
 */
export function computeVastDefaults(
  group: GroupRules,
  eligibleVaste: { offering: OfferingRules; offeringYearLevels: string[] }[],
): VastAssignment {
  // Per offering, the group's leerjaren it would participate in.
  const partByOffering = new Map<number, string[]>();
  for (const { offering, offeringYearLevels } of eligibleVaste) {
    partByOffering.set(offering.id, eligibleYearLevelsForGroup(offering, offeringYearLevels, group));
  }

  // For each leerjaar, how many NON-stackable vaste compete for it.
  const contenders = new Map<string, number[]>(); // yearLevel -> non-stackable offeringIds
  for (const { offering } of eligibleVaste) {
    if (offering.vastStackable) continue;
    for (const code of partByOffering.get(offering.id) ?? []) {
      if (!contenders.has(code)) contenders.set(code, []);
      contenders.get(code)!.push(offering.id);
    }
  }

  const result: VastAssignment = new Map();
  for (const { offering } of eligibleVaste) {
    const part = partByOffering.get(offering.id) ?? [];
    if (offering.vastStackable) {
      // Stackable: always default-on for every leerjaar it participates in.
      if (part.length) result.set(offering.id, [...part]);
      continue;
    }
    // Non-stackable: take only the leerjaren where it is the SOLE non-stackable contender.
    const owned = part.filter((code) => (contenders.get(code)?.length ?? 0) === 1);
    if (owned.length) result.set(offering.id, owned);
  }

  return result;
}

// ─── Per-GROUP "fully answered" gate (voorkeursperiode + add-ons) ─────────────────────────────
//
// A SELECTED offering is only resolved once the school has EXPLICITLY answered everything that
// offering asks for, NOT merely had it auto-selected. Voorkeur + add-ons are answered PER GROUP: a
// selection can span several class groups, and each group answers on its own. So "fully answered"
// is evaluated for one (selection, group) pair at a time. Two independent requirements, each
// applying only when the offering actually has that thing:
//   - voorkeursperiode : if the offering HAS date-blocks, THIS GROUP must have >= 1 chosen period
//                        OR an explicit "geen voorkeur" answer.
//   - add-ons          : if the offering HAS add-ons, THIS GROUP must have answered EVERY one (Ja/Nee).
// This composes ON TOP of the existing vast/keuze/opt-out resolution: a group is resolved only when
// its vast + keuze decision is made AND every offering selected FOR THAT GROUP is fully answered
// for that group.
//
// Pure + payload-agnostic: callers pass plain facts (does the offering have date-blocks / which
// add-on codes it has) and ONE GROUP's answers (does that group have a chosen period, the
// geen_voorkeur marker, which add-on codes it answered), so the client and both server services
// share one rule. `SelectionAnswers` is therefore a per-(selection, group) answer set.

/** What an offering asks the school to answer, beyond picking it. */
export type OfferingAnswerSpec = {
  /** True when the offering has voorkeursperiode date-blocks (so a voorkeur answer is required). */
  hasDateBlocks: boolean;
  /** The add-on codes this offering has (every one must be answered when selected). */
  addonCodes: string[];
};

/** One GROUP's EXPLICIT answers (on a selection) to what its offering asks. */
export type SelectionAnswers = {
  /** True when this group has >= 1 chosen "preferred" period (with a date-range) on the selection. */
  hasPreferredPeriod: boolean;
  /** True when this group has the explicit "geen voorkeur" marker on the selection. */
  hasGeenVoorkeur: boolean;
  /** The add-on codes this group has answered (Ja OR Nee) on the selection. */
  answeredAddonCodes: Set<string>;
};

/**
 * Whether a SELECTED offering is fully answered FOR ONE GROUP: that group's voorkeursperiode is
 * decided (when the offering has date-blocks) AND that group answered all of its add-ons (when it
 * has add-ons). Offerings that ask for neither are trivially fully answered.
 */
export function isOfferingFullyAnswered(spec: OfferingAnswerSpec, answers: SelectionAnswers): boolean {
  if (spec.hasDateBlocks && !(answers.hasPreferredPeriod || answers.hasGeenVoorkeur)) return false;
  if (spec.addonCodes.some((code) => !answers.answeredAddonCodes.has(code))) return false;
  return true;
}

// ─── Keuze defaults (threshold-based auto-select) ────────────────────────────────────────────

/**
 * The default keuze assignment for a group, given the school's auto-select threshold.
 *
 * Unlike vaste (which are opt-out), keuze are normally a deliberate school choice. But when a
 * group has only a SMALL number of eligible keuze offerings (1..threshold), choosing is trivial
 * and we may as well pre-select them all, so the school just confirms. When more than `threshold`
 * keuze are eligible the school must pick, so NONE are auto-selected. A threshold of 0 disables
 * auto-keuze entirely.
 *
 * Each offering takes its eligible-and-participating leerjaren of the group (same intersection as
 * vaste, via eligibleYearLevelsForGroup). Offerings that share no leerjaar with the group are
 * omitted, even within the threshold.
 *
 * @param group           the group
 * @param eligibleKeuze   the group's eligible keuze offerings, each with its own year_levels
 * @param threshold       the configured auto-select threshold (clamped 0..max by the caller)
 * @returns offeringId -> yearLevels[] for the default-on keuze (empty when none auto-select).
 */
export function computeKeuzeDefaults(
  group: GroupRules,
  eligibleKeuze: { offering: OfferingRules; offeringYearLevels: string[] }[],
  threshold: number,
): VastAssignment {
  const result: VastAssignment = new Map();
  if (threshold <= 0) return result;
  // Only count keuze that actually have a participating leerjaar for this group.
  const withYLs = eligibleKeuze
    .map(({ offering, offeringYearLevels }) => ({
      offering,
      yearLevels: eligibleYearLevelsForGroup(offering, offeringYearLevels, group),
    }))
    .filter((e) => e.yearLevels.length > 0);
  if (withYLs.length < 1 || withYLs.length > threshold) return result;
  for (const { offering, yearLevels } of withYLs) result.set(offering.id, yearLevels);
  return result;
}
